import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Building2, Palette, Gift, Crown,
  MessageCircle, Tag, ListChecks, AlertCircle, Share2, Copy, Search,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const VERTICAL_META = {
  beleza:    { label: "Salão de Beleza", emoji: "💇" },
  barbearia: { label: "Barbearia",       emoji: "💈" },
  pet:       { label: "Pet",             emoji: "🐾" },
  estudio:   { label: "Estúdio (Foto & Maquiagem)", emoji: "📸" },
};

// Ordem das categorias no wizard (passo 1)
const CATEGORY_ORDER = [
  "Beleza & Estética",
  "Estúdio (Foto & Maquiagem)",
  "Saúde",
  "Pet",
  "Fitness & Bem-estar",
  "Outros",
];

function StepDots({ step, total }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all ${i + 1 === step ? "w-8 bg-primary" : i + 1 < step ? "w-1.5 bg-primary" : "w-1.5 bg-border"}`} />
      ))}
    </div>
  );
}

function NicheCard({ bt, template, selected, onSelect }) {
  const v = bt.vertical;
  const primary = template?.default_branding?.primary_color || "262 61% 52%";
  return (
    <button
      type="button"
      onClick={() => onSelect(bt)}
      data-testid={`wizard-niche-${bt.key}`}
      className={`text-left rounded-xl border-2 p-4 transition-all h-full ${selected ? "border-primary shadow-lg" : "border-border hover:border-primary/40"}`}
      style={selected ? { background: `hsl(${primary} / 0.06)` } : undefined}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-lg grid place-items-center text-lg shrink-0"
          style={{ background: `hsl(${primary})`, color: "white" }}
        >
          {bt.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold leading-tight text-sm">{bt.label}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate">
            Base: {VERTICAL_META[v]?.label || v}
          </div>
        </div>
        {selected && <Check className="w-5 h-5 text-primary shrink-0" strokeWidth={2.4} />}
      </div>
      {bt.description && (
        <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-3">{bt.description}</p>
      )}
    </button>
  );
}

function PreviewPanel({ template }) {
  if (!template) return null;
  const t = template;
  const term = t.terminology || {};
  const primary = t.default_branding?.primary_color || "262 61% 52%";

  const sharePreview = () => {
    const url = `${window.location.origin}/preview/${t.vertical}?utm_source=master-panel&utm_campaign=share`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("Link de prévia copiado! Cole onde quiser compartilhar."),
      () => toast.error("Não foi possível copiar o link automaticamente. Use: " + url),
    );
  };

  const openPreview = () => {
    const url = `/preview/${t.vertical}?utm_source=master-panel`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Card className="p-5 rounded-xl border-border bg-card sticky top-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Prévia do que vai ser criado
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={openPreview}
            data-testid="wizard-preview-open"
            title="Abrir página pública de prévia"
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <Share2 className="w-3 h-3" /> Abrir
          </button>
          <button
            type="button"
            onClick={sharePreview}
            data-testid="wizard-preview-copy"
            title="Copiar link público compartilhável"
            className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            <Copy className="w-3 h-3" /> Copiar link
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-md" style={{ background: `hsl(${primary})` }} />
        <div className="font-display font-semibold">{t.name}</div>
      </div>

      <div className="space-y-3 text-xs">
        <div>
          <div className="flex items-center gap-1.5 font-medium mb-1.5"><Tag className="w-3.5 h-3.5" /> Termos personalizados</div>
          <div className="text-muted-foreground space-y-0.5 pl-5">
            <div>Cliente → <strong className="text-foreground">{term.client_singular}</strong></div>
            <div>Serviço → <strong className="text-foreground">{term.service_singular}</strong></div>
            <div>Atendimento → <strong className="text-foreground">{term.appointment_singular}</strong></div>
            <div>Profissional → <strong className="text-foreground">{term.resource_singular}</strong></div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 font-medium mb-1.5"><ListChecks className="w-3.5 h-3.5" /> Campos extras de cadastro</div>
          <div className="text-muted-foreground pl-5 space-y-0.5">
            {(t.default_custom_fields || []).slice(0, 6).map((f, i) => (
              <div key={i}>• {f.label} <span className="opacity-60">({f.field_type})</span></div>
            ))}
            {!(t.default_custom_fields || []).length && <div className="opacity-60">— nenhum —</div>}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 font-medium mb-1.5"><Gift className="w-3.5 h-3.5" /> Regras de fidelidade prontas</div>
          <div className="text-muted-foreground pl-5 space-y-0.5">
            {(t.default_loyalty_rules || []).map((r, i) => (
              <div key={i}>• {r.name} <strong className="text-foreground">+{r.points} pts</strong></div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 font-medium mb-1.5"><Crown className="w-3.5 h-3.5" /> Níveis de cliente</div>
          <div className="text-muted-foreground pl-5 space-y-0.5">
            {(t.default_tiers || []).map((tier, i) => (
              <div key={i}>• {tier.name} <span className="opacity-60">(a partir de {tier.min_points} pts)</span></div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 font-medium mb-1.5"><MessageCircle className="w-3.5 h-3.5" /> Modelos de mensagem</div>
          <div className="text-muted-foreground pl-5 space-y-0.5">
            {(t.default_message_templates || []).map((m, i) => (
              <div key={i}>• {m.name}</div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function MasterTenantCreateWizard({ open, setOpen, onCreated }) {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [picked, setPicked] = useState(null); // business_type object
  const [busy, setBusy] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    legal_name: "", slug: "", admin_email: "", admin_name: "Administrador", admin_password: "",
  });

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setPicked(null);
    setSearch("");
    setSuccess(null);
    setForm({ legal_name: "", slug: "", admin_email: "", admin_name: "Administrador", admin_password: "" });
    (async () => {
      try {
        const [tplRes, btRes] = await Promise.all([
          api.get("/master/templates"),
          api.get("/public/business-types").catch(() => ({ data: { items: [] } })),
        ]);
        const items = tplRes.data.items || tplRes.data || [];
        setTemplates(items);
        setBusinessTypes(btRes.data.items || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);

  // Mapa: vertical → template-pack (usado para resolver qual pack aplicar
  // quando o usuário escolhe um business_type).
  const templateByVertical = useMemo(() => {
    const map = {};
    for (const t of templates) {
      if (!map[t.vertical]) map[t.vertical] = t;
    }
    return map;
  }, [templates]);

  // Template resolvido a partir do business_type escolhido.
  const pickedTemplate = useMemo(() => {
    if (!picked) return null;
    return templateByVertical[picked.vertical] || null;
  }, [picked, templateByVertical]);

  // Nichos filtrados por busca e agrupados por categoria.
  const groupedBusinessTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? businessTypes.filter(
          (bt) =>
            (bt.label || "").toLowerCase().includes(q) ||
            (bt.description || "").toLowerCase().includes(q) ||
            (bt.category || "").toLowerCase().includes(q),
        )
      : businessTypes;
    const groups = {};
    for (const bt of filtered) {
      const cat = bt.category || "Outros";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(bt);
    }
    // Ordena categorias conforme CATEGORY_ORDER; categorias desconhecidas vão ao final.
    return CATEGORY_ORDER
      .filter((c) => groups[c])
      .map((c) => ({ category: c, items: groups[c] }))
      .concat(
        Object.keys(groups)
          .filter((c) => !CATEGORY_ORDER.includes(c))
          .map((c) => ({ category: c, items: groups[c] })),
      );
  }, [businessTypes, search]);

  const canNext = useMemo(() => {
    if (step === 1) return !!picked && !!pickedTemplate;
    if (step === 2) return form.legal_name.trim() && form.slug.trim() && form.admin_email.trim() && form.admin_password.length >= 6;
    return true;
  }, [step, picked, pickedTemplate, form]);

  const slugify = (s) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  const onLegalNameChange = (v) => {
    setForm((f) => ({
      ...f,
      legal_name: v,
      slug: f.slug ? f.slug : slugify(v),
    }));
  };

  const [success, setSuccess] = useState(null);

  const submit = async () => {
    if (!picked || !pickedTemplate) return;
    setBusy(true);
    try {
      await api.post("/master/tenants", {
        slug: form.slug,
        legal_name: form.legal_name,
        vertical: pickedTemplate.vertical,
        template_id: pickedTemplate.id,
        business_type: picked.key,
        admin_email: form.admin_email,
        admin_name: form.admin_name || "Administrador",
        admin_password: form.admin_password,
      });
      // Constrói URLs branded automáticos
      const origin = window.location.origin;
      setSuccess({
        legal_name: form.legal_name,
        slug: form.slug,
        admin_email: form.admin_email,
        admin_login_url: `${origin}/t/${form.slug}/login`,
        staff_login_url: `${origin}/t/${form.slug}/login`,
        client_portal_url: `${origin}/portal/${form.slug}/cliente/login`,
        public_booking_url: `${origin}/p/${form.slug}/agendar`,
        team_gallery_url: `${origin}/equipe/${form.slug}`,
        public_preview_url: `${origin}/empresa/${form.slug}`,
      });
      toast.success(`Empresa "${form.legal_name}" criada com sucesso!`);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyToClipboard = (txt, label = "Link") => {
    navigator.clipboard.writeText(txt).then(
      () => toast.success(`${label} copiado!`),
      () => toast.error("Falha ao copiar. Texto: " + txt),
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="wizard-dialog"
        className="max-w-5xl p-0 overflow-hidden gap-0 bg-card"
      >
        {/* Header */}
        <div className="px-7 py-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Criar empresa</div>
            <div className="font-display text-xl font-semibold mt-0.5">
              {step === 1 && "Escolha o segmento"}
              {step === 2 && "Dados da empresa"}
              {step === 3 && "Revise e finalize"}
            </div>
          </div>
          <StepDots step={step} total={3} />
        </div>

        {/* Body */}
        <div className="px-7 py-6 max-h-[70vh] overflow-y-auto">
          {success ? (
            <div className="space-y-5">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 mb-2">
                  <Check className="w-7 h-7" />
                </div>
                <h2 className="font-display text-2xl font-bold">Empresa criada com sucesso!</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>{success.legal_name}</strong> · Compartilhe os links abaixo conforme o tipo de acesso.
                </p>
              </div>

              {[
                { label: "🔑 Acesso do Administrador", url: success.admin_login_url, sub: `Email: ${success.admin_email}`, color: "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" },
                { label: "👥 Acesso da Equipe (funcionários)", url: success.staff_login_url, sub: "Mesmo link — cada funcionário usa seu próprio email/senha (criados pelo Admin em Equipe).", color: "border-sky-300 bg-sky-50 dark:bg-sky-900/20" },
                { label: "📅 Auto-agendamento (cliente final)", url: success.public_booking_url, sub: "Link público que o cliente acessa para agendar sozinho online, sem cadastro.", color: "border-teal-300 bg-teal-50 dark:bg-teal-900/20" },
                { label: "💛 Portal do Cliente", url: success.client_portal_url, sub: "Para clientes cadastrados. Senha gerada pelo Admin na lista de clientes (ícone chave).", color: "border-amber-300 bg-amber-50 dark:bg-amber-900/20" },
                { label: "📸 Galeria pública de profissionais", url: success.team_gallery_url, sub: "Mostra a equipe e portfólios para o cliente escolher com quem agendar.", color: "border-pink-300 bg-pink-50 dark:bg-pink-900/20" },
                { label: "🌐 Site da empresa", url: success.public_preview_url, sub: "Página principal. Quem entra escolhe: Equipe / Cliente / Agendar online.", color: "border-purple-300 bg-purple-50 dark:bg-purple-900/20" },
              ].map((row, i) => (
                <div key={i} className={`rounded-lg border-2 ${row.color} p-3`}>
                  <div className="font-semibold text-sm">{row.label}</div>
                  <div className="text-xs text-muted-foreground mb-2">{row.sub}</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded bg-background font-mono text-xs truncate">{row.url}</code>
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(row.url, "Link")} data-testid={`success-copy-${i}`}>Copiar</Button>
                  </div>
                </div>
              ))}

              <div className="pt-2 flex flex-wrap gap-2 justify-end border-t border-border">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="success-copy-all"
                  onClick={() => {
                    const text = [
                      `🏢 ${success.legal_name} — Acessos`,
                      ``,
                      `🔑 Administrador: ${success.admin_login_url}`,
                      `   Email: ${success.admin_email}`,
                      ``,
                      `👥 Equipe (funcionários): ${success.staff_login_url}`,
                      ``,
                      `📅 Auto-agendamento: ${success.public_booking_url}`,
                      ``,
                      `💛 Portal do Cliente: ${success.client_portal_url}`,
                      ``,
                      `📸 Galeria pública: ${success.team_gallery_url}`,
                      ``,
                      `🌐 Site da empresa: ${success.public_preview_url}`,
                    ].join("\n");
                    copyToClipboard(text, "Pacote de links");
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copiar todos os links
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="success-share-whatsapp"
                  onClick={() => {
                    const text = encodeURIComponent(
                      `*${success.legal_name}* — Seus acessos:\n\n` +
                      `🔑 Administrador: ${success.admin_login_url}\n` +
                      `(Email: ${success.admin_email})\n\n` +
                      `👥 Equipe: ${success.staff_login_url}\n\n` +
                      `📅 Auto-agendamento: ${success.public_booking_url}\n\n` +
                      `💛 Portal do Cliente: ${success.client_portal_url}\n\n` +
                      `📸 Galeria pública: ${success.team_gallery_url}\n\n` +
                      `🌐 Site da empresa: ${success.public_preview_url}`
                    );
                    window.open(`https://wa.me/?text=${text}`, "_blank");
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" /> Compartilhar no WhatsApp
                </Button>
              </div>
            </div>
          ) : (
            <>
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  {businessTypes.length} nichos disponíveis. Escolha o que mais combina com a empresa.
                </div>
                <div className="relative w-full sm:w-72">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="wizard-niche-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar nicho (ex: pilates, odonto...)"
                    className="pl-8 h-9 text-sm"
                  />
                </div>
              </div>

              {businessTypes.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline" /> Carregando nichos…
                </div>
              ) : groupedBusinessTypes.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground text-sm">
                  Nenhum nicho encontrado para "<strong>{search}</strong>".
                </div>
              ) : (
                groupedBusinessTypes.map((group) => (
                  <div key={group.category} className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-medium">
                      {group.category} <span className="opacity-50">· {group.items.length}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {group.items.map((bt) => (
                        <NicheCard
                          key={bt.key}
                          bt={bt}
                          template={templateByVertical[bt.vertical]}
                          selected={picked?.key === bt.key}
                          onSelect={setPicked}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {step === 2 && (
            <div className="grid lg:grid-cols-[1fr,360px] gap-6">
              <div className="space-y-4">
                {picked && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/20 bg-primary/5">
                    <div
                      className="w-10 h-10 rounded-lg grid place-items-center text-lg shrink-0"
                      style={{ background: `hsl(${pickedTemplate?.default_branding?.primary_color || "262 61% 52%"})`, color: "white" }}
                    >
                      {picked.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nicho escolhido</div>
                      <div className="font-display font-semibold text-sm">{picked.label}</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="wizard-change-niche"
                      onClick={() => setStep(1)}
                    >
                      Trocar
                    </Button>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Razão social</Label>
                  <Input
                    data-testid="wizard-legal-name"
                    value={form.legal_name}
                    onChange={(e) => onLegalNameChange(e.target.value)}
                    placeholder="Ex: Acme Beleza Ltda"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Endereço (link da empresa)</Label>
                  <div className="flex items-stretch border border-border rounded-md overflow-hidden bg-background">
                    <span className="px-3 text-sm text-muted-foreground self-center bg-muted/40">/t/</span>
                    <Input
                      data-testid="wizard-slug"
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                      placeholder="acme-beleza"
                      className="border-0 focus-visible:ring-0 rounded-none"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Somente letras, números e hífen. Ex.: <code className="kbd">acme-beleza</code></p>
                </div>

                <div className="pt-2 border-t border-border" />

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Nome do administrador</Label>
                    <Input
                      data-testid="wizard-admin-name"
                      value={form.admin_name}
                      onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Email do administrador</Label>
                    <Input
                      data-testid="wizard-admin-email"
                      type="email"
                      value={form.admin_email}
                      onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                      placeholder="admin@empresa.com"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Senha do administrador (mín. 6 caracteres)</Label>
                  <Input
                    data-testid="wizard-admin-password"
                    type="password"
                    minLength={6}
                    value={form.admin_password}
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                  />
                </div>
              </div>
              <PreviewPanel template={pickedTemplate} />
            </div>
          )}

          {step === 3 && (
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="p-5 rounded-xl border-border">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Resumo</div>
                <div className="space-y-3 text-sm">
                  <Row icon={Building2} label="Empresa" value={form.legal_name} />
                  <Row icon={Palette} label="Nicho" value={picked ? `${picked.emoji} ${picked.label}` : null} />
                  <Row label="Base aplicada" value={pickedTemplate ? `${pickedTemplate.name} (${VERTICAL_META[pickedTemplate.vertical]?.label || pickedTemplate.vertical})` : null} />
                  <Row label="Endereço" value={`/t/${form.slug}`} />
                  <Row label="Admin" value={`${form.admin_name} <${form.admin_email}>`} />
                </div>
                <div className="mt-5 p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs flex gap-2">
                  <AlertCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <div className="text-muted-foreground leading-relaxed">
                    Ao criar, vamos automaticamente preparar:
                    <strong className="block text-foreground mt-1">
                      {(pickedTemplate?.default_loyalty_rules || []).length} regras de fidelidade ·
                      {" "}{(pickedTemplate?.default_tiers || []).length} níveis de cliente ·
                      {" "}{(pickedTemplate?.default_message_templates || []).length} modelos de mensagem ·
                      {" "}{(pickedTemplate?.default_custom_fields || []).length} campos extras.
                    </strong>
                  </div>
                </div>
              </Card>
              <PreviewPanel template={pickedTemplate} />
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-border flex items-center justify-between gap-3 bg-muted/20">
          {success ? (
            <Button type="button" onClick={() => setOpen(false)} className="ml-auto" data-testid="wizard-finish">
              <Check className="w-4 h-4 mr-2" /> Concluir
            </Button>
          ) : (
            <>
          {step > 1 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} className="gap-2" data-testid="wizard-back">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </Button>
          ) : <div />}
          {step < 3 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canNext}
              className="gap-2"
              data-testid="wizard-next"
            >
              Continuar <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={submit}
              disabled={busy}
              className="gap-2"
              data-testid="wizard-submit"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              <Check className="w-4 h-4" /> Criar empresa
            </Button>
          )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-xs uppercase tracking-wider text-muted-foreground w-28 shrink-0 pt-0.5 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-medium flex-1 break-words">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}
